import { useQuery } from '@tanstack/react-query';
import { type ContextType, createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { usePrevious } from 'react-use';

import { URL_PARAM_COLLECTION } from '$const';
import useFavouriteService from '~extension/favourite/hook/useFavouriteService';
import useUrlSearchParam from '~hooks/useUrlSearchParam';
import { queries } from '~modules/media-library/controller/api/Queries';
import { type AssetId } from '~modules/media-library/model/Asset';
import { type Settings } from '~modules/media-library/model/Settings';
import { ViewContext } from '~modules/media-library/model/ViewContext';

export const MediaLibrarySettingsContext = createContext<
  | {
      // values
      realm: string;
      idCollection?: AssetId;
      searchTermTop?: string;
      isLoading: boolean;
      isError: boolean;
      filterList: Settings['filters'];
      tabFilter: Settings['tabFilter'];
      additionalSettings: Settings['additionalSettings'];
      sortOptions: Settings['sortOptions'];
      viewVariants: Settings['viewVariants'];
      isFavouritesEnabled: boolean;
      // handlers
      setCollectionId: (_id: AssetId) => void;
      unsetCollectionId: () => void;
      setTopSearchTerm: (_searchTerm: string) => void;
      viewContext: ViewContext;
      setViewContext: (_viewContext: ViewContext) => void;
    }
  | undefined
>(undefined);

export function MediaLibrarySettingProvider({ children, realm }: { children: ReactNode; realm: string }) {
  // Read URL param and prepare collection state
  const paramCollection = useUrlSearchParam(URL_PARAM_COLLECTION);
  const [idCollection, setIdCollection] = useState<AssetId>(paramCollection);
  // to keep search term of the global search, if a collection is open
  const [searchTermTop, setSearchTermTop] = useState<string>();
  const prevIdCollection = usePrevious(idCollection);
  const setCollectionId = useCallback((id: AssetId) => setIdCollection(id), []);
  const unsetCollectionId = useCallback(() => setIdCollection(undefined), []);
  const setTopSearchTerm = useCallback((searchTerm: string) => setSearchTermTop(searchTerm), []);
  const [viewContext, setViewContextState] = useState<ViewContext>(ViewContext.DEFAULT);
  const setViewContext = useCallback((viewContext: ViewContext) => setViewContextState(viewContext), []);

  // Favourite enabled
  const { isFavouriteAddonEnabled } = useFavouriteService();

  // Effect for "restoring" the scroll position before the user entered a collection
  const { data: scrollRestoreElement } = useQuery({
    ...queries.mediaLibrary.scrollRestore(prevIdCollection),
    enabled: !Boolean(idCollection) && Boolean(prevIdCollection),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
  useEffect(() => {
    if (scrollRestoreElement === undefined) return;
    scrollRestoreElement?.scrollIntoView?.({ behavior: 'auto', block: 'center', inline: 'nearest' });
  }, [scrollRestoreElement]);

  // Effect for scrolling to top when entering a collection
  const { data: scrollCollectionElement } = useQuery({
    ...queries.mediaLibrary.scrollCollection(idCollection),
    enabled: Boolean(idCollection),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
  useEffect(() => {
    if (scrollCollectionElement === undefined) return;
    scrollCollectionElement?.scrollIntoView?.({ behavior: 'auto', block: 'start', inline: 'nearest' });
  }, [scrollCollectionElement]);

  // Effect to update URL
  useEffect(() => {
    if (paramCollection === idCollection) return;
    if (!idCollection) {
      window.history.replaceState({}, '', `${window.location.pathname}`);
    }
    if (!!idCollection) {
      window.history.replaceState({}, '', `${window.location.pathname}?${URL_PARAM_COLLECTION}=${idCollection}`);
    }
  }, [idCollection, paramCollection]);

  // Fetch settings
  const { data, isLoading, isError } = useQuery({
    ...queries.mediaLibrary.settings(realm, idCollection),
    retry: false,
    gcTime: Infinity,
    staleTime: Infinity,
  });

  const value: ContextType<typeof MediaLibrarySettingsContext> = useMemo(
    () => ({
      realm,
      idCollection,
      setCollectionId,
      unsetCollectionId,
      searchTermTop,
      setTopSearchTerm,
      isLoading,
      isError,
      filterList: data?.filters || [],
      tabFilter: data?.tabFilter || null,
      additionalSettings: data?.additionalSettings || null,
      sortOptions: data?.sortOptions,
      viewVariants: data?.viewVariants || [],
      isFavouritesEnabled: isFavouriteAddonEnabled(),
      viewContext,
      setViewContext,
    }),
    [
      realm,
      idCollection,
      setCollectionId,
      unsetCollectionId,
      searchTermTop,
      setTopSearchTerm,
      isLoading,
      isError,
      data?.filters,
      data?.tabFilter,
      data?.additionalSettings,
      data?.sortOptions,
      data?.viewVariants,
      isFavouriteAddonEnabled,
      viewContext,
      setViewContext,
    ],
  );

  return <MediaLibrarySettingsContext.Provider value={value}>{children}</MediaLibrarySettingsContext.Provider>;
}
